import { Mail, Shield, AlertTriangle, CheckCircle, ArrowLeft, Home } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';

const DeleteAccountRequest = () => {
  const navigate = useNavigate();

  const handleEmailRequest = () => {
    const subject = encodeURIComponent('Account Deletion Request - Am I OK? - Health Advisor');
    const body = encodeURIComponent(`Hello Am I OK? - Health Advisor Support Team,

I would like to request the deletion of my account and all associated data from the Am I OK? - Health Advisor application.

Account Details:
- Email address: [Please provide your registered email address]
- Full Name: [Please provide your full name]
- Additional Information: [Any additional details that may help identify your account]

I understand that this action is permanent and cannot be undone.

Thank you for your assistance.

Best regards,
[Your Name]`);
    
    window.location.href = `mailto:jm@magnavisio.co.uk?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-12 px-4 max-w-4xl">
        {/* Navigation Header */}
        <div className="flex gap-2 mb-8">
          <Button variant="outline" size="sm" onClick={() => navigate('/account-settings')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Account Settings
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <Home className="h-4 w-4 mr-2" />
            Home
          </Button>
        </div>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">Account Deletion Request</h1>
          <p className="text-xl text-muted-foreground">
            Request deletion of your Am I OK? - Health Advisor account and associated data
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Developer: Health Assessment Solutions
          </p>
        </div>

        <div className="grid gap-6 max-w-2xl mx-auto">
          {/* Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Your Privacy Rights
              </CardTitle>
              <CardDescription>
                We respect your privacy and your right to control your personal data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                As a user of the Am I OK? - Health Advisor application, you have the right to request 
                deletion of your account and all associated personal data at any time.
              </p>
              <p className="text-sm text-muted-foreground">
                This includes your profile information, assessment history, and any other data 
                we have collected about you through your use of our application.
              </p>
            </CardContent>
          </Card>

          {/* Warning Card */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> Account deletion is permanent and cannot be undone. 
              All your assessment history and profile data will be permanently removed from our systems.
            </AlertDescription>
          </Alert>

          {/* Request Methods */}
          <Card>
            <CardHeader>
              <CardTitle>How to Request Account Deletion</CardTitle>
              <CardDescription>
                Choose one of the following methods to submit your deletion request
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* In-App Method */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Method 1: In-App Deletion (Recommended)
                </h3>
                <p className="text-sm text-muted-foreground">
                  If you have the app installed and can log in:
                </p>
                <ol className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>1. Open the Am I OK? - Health Advisor app</li>
                  <li>2. Go to Account Settings</li>
                  <li>3. Scroll down to "Danger Zone"</li>
                  <li>4. Click "Delete Account Permanently"</li>
                  <li>5. Confirm your deletion request</li>
                </ol>
              </div>

              {/* Email Method */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  Method 2: Email Request
                </h3>
                <p className="text-sm text-muted-foreground">
                  If you cannot access the app or prefer to email us:
                </p>
                <Button onClick={handleEmailRequest} className="w-full">
                  <Mail className="h-4 w-4 mr-2" />
                  Send Deletion Request Email
                </Button>
                <p className="text-xs text-muted-foreground">
                  This will open your email client with a pre-filled deletion request
                </p>
              </div>
            </CardContent>
          </Card>

          {/* What Gets Deleted */}
          <Card>
            <CardHeader>
              <CardTitle>What Data Will Be Deleted</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-600" />
                  Your profile information (name, email, preferences)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-600" />
                  All assessment history and results
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-600" />
                  Health insights and personalized data
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-600" />
                  Account settings and preferences
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-600" />
                  Any other personal data associated with your account
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Processing Time */}
          <Card>
            <CardHeader>
              <CardTitle>Processing Time</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Account deletion requests are typically processed within <strong>30 days</strong> of receipt. 
                You will receive a confirmation email once your account and data have been permanently deleted.
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Need Help?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                If you have any questions about the account deletion process or need assistance, 
                please contact our support team:
              </p>
              <div className="space-y-2 text-sm">
                <p><strong>Email:</strong> jm@magnavisio.co.uk</p>
                <p><strong>Response Time:</strong> Within 48 hours</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
          <div className="text-center mt-12 pt-8 border-t">
          <p className="text-sm text-muted-foreground">
            This page complies with Google Play Developer Policy requirements for account deletion requests for Am I OK? - Health Advisor.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            App Store Listing: Am I OK? - Health Advisor | Developer: Health Assessment Solutions
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountRequest;